import { createSelector } from '@reduxjs/toolkit';
import { selectResultFromPopularActivitiesState } from 'component/page/search/store/search.selectors';
import { ActivityCardProps } from 'core/type/ActivityCard.types';
import { selectConfigFromBootstrap, selectFeatureBehaviorFromBootstrap, selectTopSubcategoriesFromBootstrap } from 'store/slice/bootstrap';
import { Breakpoint, ScreenSize, selectBreakpoint, selectScreenSize } from 'store/slice/screen';
import { BootstrapConfig, Geo, TopSubcategory } from 'core/type/BootstrapConfig.types';
import { isB, isOn } from 'core/window/bootstrapConfig';
import { SuggestionItem } from 'core/type/SuggestionResult.types';
import { TextSearchType } from 'core/type/TextSearch.types';
import { ActivityWithContent } from 'gql/autogen/ActivityWithContent';
import { mapTitleFromContent } from 'gql/service/graph/activityService';
import { mapCategoryCms, mapCategoryIcon } from 'core-framework/util/EmptyStateSuggestion';
import { FeatureName } from 'core/constant/FeatureName';
import { ExperimentId } from 'core/type/ExperimentId';

interface StateProps {
    config: BootstrapConfig;
    screenSize: ScreenSize;
    breakPoint: Breakpoint;
    isSearchBoxFeatureGroup: boolean;
    isPageContainerSpacing: boolean;
    isNotMobile: boolean;
    hasNoTopSubCategories: boolean;
    isFixOverlayIssue: boolean;
    popularActivities?: ActivityCardProps[];
    topSubcategories?: TopSubcategory[];
    isRemoveCityPageImage: boolean;
}

export type CityPageProps = StateProps;

export const mapSuggestionItem = (geo: Geo, path: string, selectedActivity?: ActivityWithContent): SuggestionItem => {
    if (selectedActivity) {
        const title = mapTitleFromContent(selectedActivity.content);
        return {
            icon: mapCategoryIcon(selectedActivity.content?.activity?.categories),
            categoryCms: mapCategoryCms(selectedActivity.content?.activity?.categories),
            id: selectedActivity.masterActivityId || -1,
            activityId: selectedActivity.masterActivityId || -1,
            activityName: title,
            cityId: geo.id,
            cityName: geo.cityName,
            countryName: geo.countryName || '',
            isCityPageEligible: true,
            type: TextSearchType.Activity,
            url: path,
            supplierId: selectedActivity.masterSupplierId,
        };
    }

    return {
        icon: 'fill.activity.ferris-wheel',
        categoryCms: 0,
        id: geo.id,
        activityId: -1,
        activityName: '',
        cityId: geo.id,
        cityName: geo.cityName,
        countryName: geo.countryName || '',
        isCityPageEligible: true,
        type: TextSearchType.City,
        url: path,
    };
};

export const mapStateToProps = createSelector(
    selectResultFromPopularActivitiesState,
    selectConfigFromBootstrap,
    selectScreenSize,
    selectBreakpoint,
    selectFeatureBehaviorFromBootstrap,
    selectTopSubcategoriesFromBootstrap,
    (popularActivities, config, screenSize, breakPoint, featureBehaviours, topSubcategories): StateProps => {
        const isSearchBoxFeatureGroup = featureBehaviours.SearchBoxFeatureGroup;
        const isPageContainerSpacing = isOn(FeatureName.PageContainerSpacing, config);
        const isNotMobile = !screenSize.mobile;
        const hasNoTopSubCategories = !topSubcategories || topSubcategories.length === 0;

        const isFixOverlayIssue = isB(ExperimentId.ACT_4894, config.experiments) && screenSize.mobile;
        const isRemoveCityPageImage = isB(ExperimentId.ACT_5130, config.experiments);

        return {
            popularActivities,
            config,
            screenSize,
            breakPoint,
            topSubcategories,
            isSearchBoxFeatureGroup,
            isPageContainerSpacing,
            isNotMobile,
            hasNoTopSubCategories,
            isFixOverlayIssue,
            isRemoveCityPageImage,
        };
    },
);
